import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GoDash } from "react-icons/go";
import clsx from "clsx";
import { useParams } from "react-router-dom";
import { isEmpty } from "lodash";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import useLocalStorage from "@/hooks/useLocalStorage";
import { getListingReactions, getReactionCounts } from "@/services/ReactionService";
import { unicodeToEmoji, emojiToUnicode, REACTION_EMOJIS } from "@/constants/reaction-unicode";

interface ReactionUserModalProps {
    show: boolean;
    onClose: () => void;
    propertyId: string;
    initialEmoji?: string;
}

const ReactionUserModal: React.FC<ReactionUserModalProps> = ({ show, onClose, propertyId, initialEmoji = "all" }) => {
    const [selectedEmoji, setSelectedEmoji] = useState<string>(initialEmoji);
    const [reactions, setReactions] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);

    const [emojis, setEmojis] = useState<{ label: string; value: string; count: number }[]>([]);
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();
    const [socialState] = useLocalStorage("social-state", {} as any);
    const { listing_address } = useParams();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const modalRef = useRef<HTMLDivElement | null>(null);

    let pageName = "Property Feed - Trending";
    if (!isEmpty(listing_address)) {
        pageName = "Property Details";
    } else if ((socialState as any)?.activeTab === "following") {
        pageName = "Property Feed - Following";
    }

    // Click away listener
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (show && modalRef.current && !modalRef.current.contains(event.target as Node)) {
                onClose();
            }
        };

        if (show) {
            document.addEventListener("mousedown", handleClickOutside);
            document.addEventListener("touchstart", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("touchstart", handleClickOutside);
        };
    }, [show, onClose]);

    // Fetch reaction counts to build the emoji tabs
    const fetchReactionCounts = useCallback(async () => {
        try {
            const response = await getReactionCounts("Property", propertyId);
            const counts: Record<string, number> = {};
            let totalCount = 0;

            // Convert Unicode counts to emoji counts
            Object.entries(response).forEach(([unicode, count]) => {
                const emoji = unicodeToEmoji(unicode);
                counts[emoji] = count as number;
                totalCount += count as number;
            });

            // Build emoji tabs
            const emojiTabs: { label: string; value: string; count: number }[] = [
                { label: "All", value: "all", count: totalCount },
            ];
            REACTION_EMOJIS.forEach((emoji) => {
                if (counts[emoji] > 0) {
                    emojiTabs.push({ label: emoji, value: emoji, count: counts[emoji] });
                }
            });
            setEmojis(emojiTabs);
        } catch (error) {
            console.error("Failed to fetch reaction counts:", error);
            setError("Failed to load reaction data");
        }
    }, [propertyId]);

    // Fetch reactions for the selected emoji
    const fetchReactions = useCallback(
        async (emoji: string, page = 1, append = false) => {
            if (!propertyId) return;

            setLoading(true);
            setError(null);

            try {
                const reactionType = emoji === "all" ? null : emojiToUnicode(emoji);
                const response = await getListingReactions("Property", propertyId, reactionType as any, page, 20);

                const newReactions = response.data.data.map((reaction: any) => ({
                    id: reaction.id,
                    name: reaction.user.nickname || reaction.user.emailAddress || "Anonymous",
                    emoji: unicodeToEmoji(reaction.reactionType),
                    createdAt: reaction.createdAt,
                }));

                if (append) {
                    setReactions((prev) => [...prev, ...newReactions]);
                } else {
                    setReactions(newReactions);
                }

                setHasMore(((response as any).data?.hasNextPage as boolean) ?? false);
                setCurrentPage(page);
            } catch (error) {
                console.error("Failed to fetch reactions:", error);
                setError("Failed to load reactions");
            } finally {
                setLoading(false);
            }
        },
        [propertyId],
    );

    // Handle tab click
    const handleTabClick = useCallback(
        (emoji: string) => {
            setSelectedEmoji(emoji);
            setCurrentPage(1);
            setHasMore(true);
            fetchReactions(emoji, 1, false);
        },
        [fetchReactions],
    );

    // Handle infinite scroll
    const handleScroll = useCallback(() => {
        if (!containerRef.current || loading || !hasMore) return;

        const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 50) {
            fetchReactions(selectedEmoji, currentPage + 1, true);
        }
    }, [loading, hasMore, selectedEmoji, currentPage, fetchReactions]);

    // Update selectedEmoji when initialEmoji prop changes
    useEffect(() => {
        setSelectedEmoji(initialEmoji);
    }, [initialEmoji]);

    // Initialize data when modal opens
    useEffect(() => {
        if (show && propertyId) {
            fetchReactionCounts();
            fetchReactions(selectedEmoji, 1, false);
        }
    }, [show, propertyId, selectedEmoji, fetchReactionCounts, fetchReactions]);

    // Analytics tracking
    useEffect(() => {
        if (show) {
            sendMixpanelEvent("View All Reactions", {
                isAuthenticated: !!userState?.id,
                user_id: userState?.id || null,
                nickname: userState?.nickname || null,
                email: userState?.emailAddress || null,
                pageName,
            });
        }
    }, [show, userState, sendMixpanelEvent, pageName]);

    // Add scroll listener for infinite scroll
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener("scroll", handleScroll);
            return () => container.removeEventListener("scroll", handleScroll);
        }
    }, [handleScroll]);

    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ y: "100%" }}
                    animate={{ y: 0 }}
                    exit={{ y: "100%" }}
                    transition={{ duration: 0.3 }}
                    className="tw-flex tw-flex-col tw-justify-self-center tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-[9999] tw-bg-white tw-rounded-t-2xl tw-shadow-lg tw-h-[45vh] tw-overflow-hidden tw-w-full md:tw-w-[600px] tw-select-none"
                    ref={modalRef}
                >
                    {/* Header */}
                    <div className="tw-flex tw-justify-center tw-items-center tw-border-b tw-flex-shrink-0">
                        <button
                            className="tw-bg-white tw-text-gray-400 tw-h-4 tw-flex tw-justify-center tw-items-center"
                            onClick={onClose}
                        >
                            <GoDash className="tw-w-14 tw-h-14" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="tw-flex tw-overflow-x-auto tw-px-2 tw-py-2 tw-flex-shrink-0 tw-select-none">
                        {emojis.map((emoji) => (
                            <button
                                key={emoji.value}
                                onClick={() => handleTabClick(emoji.value)}
                                className={clsx(
                                    "tw-px-3 tw-py-1 tw-rounded-full tw-border tw-text-sm tw-bg-white tw-transition-all tw-relative tw-select-none",
                                    selectedEmoji === emoji.value
                                        ? "tw-text-purple-600 tw-font-semibold"
                                        : "tw-border-gray-300 tw-text-gray-600 hover:tw-bg-gray-50",
                                )}
                            >
                                <span className="tw-select-none">{emoji.label}</span>{" "}
                                <span className="tw-select-none">{emoji.count}</span>
                                {selectedEmoji === emoji.value && (
                                    <div className="tw-absolute tw-bottom-0 tw-left-0 tw-w-full tw-h-[2px] tw-bg-[#7927D7]" />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Users */}
                    <div ref={containerRef} className="tw-overflow-y-auto tw-flex-1 tw-p-4 tw-space-y-4 tw-select-none">
                        {error && <div className="tw-text-center tw-text-red-500 tw-py-4 tw-select-none">{error}</div>}

                        {loading && reactions.length === 0 && (
                            <div className="tw-text-center tw-text-gray-500 tw-py-4 tw-select-none">
                                Loading reactions...
                            </div>
                        )}

                        {!loading && reactions.length === 0 && !error && (
                            <div className="tw-text-center tw-text-gray-500 tw-py-4 tw-select-none">
                                No reactions found
                            </div>
                        )}

                        {reactions.map((user) => (
                            <div
                                key={`${user.id}-${user.createdAt}`}
                                className="tw-flex tw-items-center tw-gap-4 tw-rounded-lg tw-select-none"
                            >
                                <span className="tw-font-medium tw-text-sm tw-flex-1 tw-select-none">{user.name}</span>
                                {selectedEmoji === "all" && (
                                    <span className="tw-text-lg tw-select-none">{user.emoji}</span>
                                )}
                            </div>
                        ))}

                        {loading && reactions.length > 0 && (
                            <div className="tw-text-center tw-text-gray-500 tw-py-4 tw-select-none">
                                Loading more...
                            </div>
                        )}

                        {!hasMore && reactions.length > 0 && (
                            <div className="tw-text-center tw-text-gray-400 tw-py-2 tw-text-sm tw-select-none">
                                No more reactions
                            </div>
                        )}
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ReactionUserModal;
